import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ETypeToast } from 'app/enum';
import { getLabelError } from 'helpers/funcs';
import { dashboardApi } from 'api/dashboardApi';
import { setShowToast } from 'store/toast/toastSlice';

export type DashboardCounts = {
  canceledReservation: number;
  uncollectedSurvey: number;
  lowReservationRate: number;
};

export type EventProgressSummary = {
  overall_interview_rate: number;
  required_interviews: {
    this_month: number;
    next_month: number;
  };
  open_interview_slots: {
    this_month: number;
    next_month: number;
  };
};

export type EventProgressItem = {
  _id: string;
  event_id: string;
  client_id: string;
  facility_id: string;
  event_name: string;
  client_name: string;
  facility_name: string;
  end_date: string;
  target_audience: number;
  interview_count: { total: number; delta: number };
  response_count: { total: number; delta: number };
  feedback_completion_count: { total: number; delta: number };
};

export type InterviewProgressItem = {
  id: string;
  startTime: string;
  endTime: string;
  interviewStatus: string;
  employeeProgressId: string;
  consultantId: string;
  employeeId: string;
  eventId: string;
  clientId: string;
  clientName: string;
  facilityId: string;
  facilityName: string;
  eventName: string;
  consultantName: string;
  currentStatus: string;
};

export type ConsultantAvailabilityEvent = {
  event_id: string;
  event_name: string;
  facility_id: string;
  facility_name: string;
  client_id: string;
  client_name: string;
  interview_count: number;
  interview_rate: number;
};

export type ConsultantAvailabilityItem = {
  consultant_id: string;
  name: string;
  open_slots: number;
  interview_count: number;
  events: ConsultantAvailabilityEvent[];
};

export type DashboardState = {
  loading: boolean;
  loadingCanceledReservations: boolean;
  loadingPendingSurveys: boolean;
  loadingLowReservationRates: boolean;
  loadingEventProgress: boolean;
  loadingMoreEventProgress: boolean;
  loadingInterviewProgress: boolean;
  loadingMoreInterviewProgress: boolean;
  loadingConsultantAvailability: boolean;
  loadingMoreConsultantAvailability: boolean;
  counts: DashboardCounts;
  canceledReservations: any[];
  pendingSurveys: any[];
  lowReservationRates: any[];
  eventProgressSummary: EventProgressSummary;
  eventProgressRows: EventProgressItem[];
  eventProgressPage: number;
  eventProgressTotalPage: number;
  eventProgressTotal: number;
  interviewProgressRows: InterviewProgressItem[];
  interviewProgressPage: number;
  interviewProgressTotalPage: number;
  interviewProgressTotal: number;
  consultantAvailabilityRows: ConsultantAvailabilityItem[];
  consultantAvailabilityPage: number;
  consultantAvailabilityTotalPage: number;
  consultantAvailabilityTotal: number;
};

const initialState: DashboardState = {
  loading: false,
  loadingCanceledReservations: false,
  loadingPendingSurveys: false,
  loadingLowReservationRates: false,
  loadingEventProgress: false,
  loadingMoreEventProgress: false,
  loadingInterviewProgress: false,
  loadingMoreInterviewProgress: false,
  loadingConsultantAvailability: false,
  loadingMoreConsultantAvailability: false,
  counts: {
    canceledReservation: 0,
    uncollectedSurvey: 0,
    lowReservationRate: 0,
  },
  canceledReservations: [],
  pendingSurveys: [],
  lowReservationRates: [],
  eventProgressSummary: {
    overall_interview_rate: 0,
    required_interviews: {
      this_month: 0,
      next_month: 0,
    },
    open_interview_slots: {
      this_month: 0,
      next_month: 0,
    },
  },
  eventProgressRows: [],
  eventProgressPage: 1,
  eventProgressTotalPage: 1,
  eventProgressTotal: 0,
  interviewProgressRows: [],
  interviewProgressPage: 1,
  interviewProgressTotalPage: 1,
  interviewProgressTotal: 0,
  consultantAvailabilityRows: [],
  consultantAvailabilityPage: 1,
  consultantAvailabilityTotalPage: 1,
  consultantAvailabilityTotal: 0,
};

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getTopCounts = createAsyncThunk('dashboard/getTopCounts', async (_, thunkAPI) => {
  thunkAPI.dispatch(setLoading(true));
  await dashboardApi
    .getTopCounts()
    .then((response) => {
      const data = response?.data?.data ?? {};
      thunkAPI.dispatch(
        setTopCounts({
          canceledReservation: toNumber(data.canceled_reservation),
          uncollectedSurvey: toNumber(data.pending_survey),
          lowReservationRate: toNumber(data.low_reservation_rate),
        }),
      );
    })
    .catch((response) => {
      thunkAPI.dispatch(
        setShowToast({
          label: getLabelError(response?.response?.data?.statusCode ?? ''),
          type: ETypeToast.Error,
        }),
      );
    });
  thunkAPI.dispatch(setLoading(false));
});

export const getCanceledReservations = createAsyncThunk(
  'dashboard/getCanceledReservations',
  async (params: { date?: string } = {}, thunkAPI) => {
    thunkAPI.dispatch(setLoadingCanceledReservations(true));
    await dashboardApi
      .getCanceledReservations(params.date)
      .then((response) => {
        thunkAPI.dispatch(setCanceledReservations(response?.data?.data ?? []));
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response?.response?.data?.statusCode ?? ''),
            type: ETypeToast.Error,
          }),
        );
      });
    thunkAPI.dispatch(setLoadingCanceledReservations(false));
  },
);

export const getPendingSurveys = createAsyncThunk(
  'dashboard/getPendingSurveys',
  async (params: { date?: string } = {}, thunkAPI) => {
    thunkAPI.dispatch(setLoadingPendingSurveys(true));
    await dashboardApi
      .getPendingSurveys(params.date)
      .then((response) => {
        thunkAPI.dispatch(setPendingSurveys(response?.data?.data ?? []));
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response?.response?.data?.statusCode ?? ''),
            type: ETypeToast.Error,
          }),
        );
      });
    thunkAPI.dispatch(setLoadingPendingSurveys(false));
  },
);

export const getLowReservationRates = createAsyncThunk(
  'dashboard/getLowReservationRates',
  async (_, thunkAPI) => {
    thunkAPI.dispatch(setLoadingLowReservationRates(true));
    await dashboardApi
      .getLowReservationRates()
      .then((response) => {
        thunkAPI.dispatch(setLowReservationRates(response?.data?.data ?? []));
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response?.response?.data?.statusCode ?? ''),
            type: ETypeToast.Error,
          }),
        );
      });
    thunkAPI.dispatch(setLoadingLowReservationRates(false));
  },
);

export const getEventProgress = createAsyncThunk(
  'dashboard/getEventProgress',
  async (
    params: { search?: string; page?: number; perPage?: number; loadMore?: boolean } = {},
    thunkAPI,
  ) => {
    const page = params.page ?? 1;
    const perPage = params.perPage ?? 20;
    const loadMore = !!params.loadMore;
    thunkAPI.dispatch(loadMore ? setLoadingMoreEventProgress(true) : setLoadingEventProgress(true));
    await dashboardApi
      .getEventProgress({
        search: params.search,
        page,
        perPage,
      })
      .then((response) => {
        const summary = response?.data?._metadata?.summary;
        const pagination = response?.data?._metadata?.pagination;
        thunkAPI.dispatch(
          setEventProgressSummary({
            overall_interview_rate: toNumber(summary?.overall_interview_rate),
            required_interviews: {
              this_month: toNumber(summary?.required_interviews?.this_month),
              next_month: toNumber(summary?.required_interviews?.next_month),
            },
            open_interview_slots: {
              this_month: toNumber(summary?.open_interview_slots?.this_month),
              next_month: toNumber(summary?.open_interview_slots?.next_month),
            },
          }),
        );
        thunkAPI.dispatch(
          setEventProgressPagination({
            page: toNumber(pagination?.page) || page,
            totalPage: toNumber(pagination?.totalPage) || 1,
            total: toNumber(pagination?.total),
          }),
        );
        thunkAPI.dispatch(
          setEventProgressRows({
            rows: response?.data?.data ?? [],
            loadMore,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response?.response?.data?.statusCode ?? ''),
            type: ETypeToast.Error,
          }),
        );
      });
    thunkAPI.dispatch(
      loadMore ? setLoadingMoreEventProgress(false) : setLoadingEventProgress(false),
    );
  },
);

export const getInterviewProgress = createAsyncThunk(
  'dashboard/getInterviewProgress',
  async (
    params: {
      date?: string;
      search?: string;
      page?: number;
      perPage?: number;
      loadMore?: boolean;
    } = {},
    thunkAPI,
  ) => {
    const page = params.page ?? 1;
    const perPage = params.perPage ?? 20;
    const loadMore = !!params.loadMore;
    thunkAPI.dispatch(
      loadMore ? setLoadingMoreInterviewProgress(true) : setLoadingInterviewProgress(true),
    );
    await dashboardApi
      .getInterviewProgress({
        date: params.date,
        search: params.search,
        page,
        perPage,
      })
      .then((response) => {
        const pagination = response?.data?._metadata?.pagination;
        thunkAPI.dispatch(
          setInterviewProgressRows({
            rows: response?.data?.data ?? [],
            loadMore,
          }),
        );
        thunkAPI.dispatch(
          setInterviewProgressPagination({
            page: toNumber(pagination?.page) || page,
            totalPage: toNumber(pagination?.totalPage) || 1,
            total: toNumber(pagination?.total),
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response?.response?.data?.statusCode ?? ''),
            type: ETypeToast.Error,
          }),
        );
      });
    thunkAPI.dispatch(
      loadMore ? setLoadingMoreInterviewProgress(false) : setLoadingInterviewProgress(false),
    );
  },
);

export const getConsultantAvailability = createAsyncThunk(
  'dashboard/getConsultantAvailability',
  async (
    params: {
      month?: number;
      year?: number;
      search?: string;
      page?: number;
      perPage?: number;
      orderBy?: ('interviewCount' | 'openSlots')[];
      loadMore?: boolean;
    } = {},
    thunkAPI,
  ) => {
    const page = params.page ?? 1;
    const perPage = params.perPage ?? 20;
    const loadMore = !!params.loadMore;
    const orderBy = params.orderBy ?? ['interviewCount', 'openSlots'];
    thunkAPI.dispatch(
      loadMore
        ? setLoadingMoreConsultantAvailability(true)
        : setLoadingConsultantAvailability(true),
    );
    await dashboardApi
      .getConsultantAvailability({
        month: params.month,
        year: params.year,
        search: params.search,
        page,
        perPage,
        orderBy,
      })
      .then((response) => {
        const pagination = response?.data?._metadata?.pagination;
        thunkAPI.dispatch(
          setConsultantAvailabilityRows({
            rows: response?.data?.data ?? [],
            loadMore,
          }),
        );
        thunkAPI.dispatch(
          setConsultantAvailabilityPagination({
            page: toNumber(pagination?.page) || page,
            totalPage: toNumber(pagination?.totalPage) || 1,
            total: toNumber(pagination?.total),
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response?.response?.data?.statusCode ?? ''),
            type: ETypeToast.Error,
          }),
        );
      });
    thunkAPI.dispatch(
      loadMore
        ? setLoadingMoreConsultantAvailability(false)
        : setLoadingConsultantAvailability(false),
    );
  },
);

const slice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoadingCanceledReservations: (state, action: PayloadAction<boolean>) => {
      state.loadingCanceledReservations = action.payload;
    },
    setLoadingPendingSurveys: (state, action: PayloadAction<boolean>) => {
      state.loadingPendingSurveys = action.payload;
    },
    setLoadingLowReservationRates: (state, action: PayloadAction<boolean>) => {
      state.loadingLowReservationRates = action.payload;
    },
    setLoadingEventProgress: (state, action: PayloadAction<boolean>) => {
      state.loadingEventProgress = action.payload;
    },
    setLoadingMoreEventProgress: (state, action: PayloadAction<boolean>) => {
      state.loadingMoreEventProgress = action.payload;
    },
    setLoadingInterviewProgress: (state, action: PayloadAction<boolean>) => {
      state.loadingInterviewProgress = action.payload;
    },
    setLoadingMoreInterviewProgress: (state, action: PayloadAction<boolean>) => {
      state.loadingMoreInterviewProgress = action.payload;
    },
    setLoadingConsultantAvailability: (state, action: PayloadAction<boolean>) => {
      state.loadingConsultantAvailability = action.payload;
    },
    setLoadingMoreConsultantAvailability: (state, action: PayloadAction<boolean>) => {
      state.loadingMoreConsultantAvailability = action.payload;
    },
    setTopCounts: (state, action: PayloadAction<DashboardCounts>) => {
      state.counts = action.payload;
    },
    setCanceledReservations: (state, action: PayloadAction<any[]>) => {
      state.canceledReservations = action.payload;
    },
    setPendingSurveys: (state, action: PayloadAction<any[]>) => {
      state.pendingSurveys = action.payload;
    },
    setLowReservationRates: (state, action: PayloadAction<any[]>) => {
      state.lowReservationRates = action.payload;
    },
    setEventProgressSummary: (state, action: PayloadAction<EventProgressSummary>) => {
      state.eventProgressSummary = action.payload;
    },
    setEventProgressRows: (
      state,
      action: PayloadAction<{ rows: EventProgressItem[]; loadMore?: boolean }>,
    ) => {
      const { rows, loadMore } = action.payload;
      state.eventProgressRows = loadMore ? [...state.eventProgressRows, ...rows] : rows;
    },
    setEventProgressPagination: (
      state,
      action: PayloadAction<{ page: number; totalPage: number; total: number }>,
    ) => {
      state.eventProgressPage = action.payload.page;
      state.eventProgressTotalPage = action.payload.totalPage;
      state.eventProgressTotal = action.payload.total;
    },
    setInterviewProgressRows: (
      state,
      action: PayloadAction<{ rows: InterviewProgressItem[]; loadMore?: boolean }>,
    ) => {
      const { rows, loadMore } = action.payload;
      state.interviewProgressRows = loadMore ? [...state.interviewProgressRows, ...rows] : rows;
    },
    setInterviewProgressPagination: (
      state,
      action: PayloadAction<{ page: number; totalPage: number; total: number }>,
    ) => {
      state.interviewProgressPage = action.payload.page;
      state.interviewProgressTotalPage = action.payload.totalPage;
      state.interviewProgressTotal = action.payload.total;
    },
    setConsultantAvailabilityRows: (
      state,
      action: PayloadAction<{ rows: ConsultantAvailabilityItem[]; loadMore?: boolean }>,
    ) => {
      const { rows, loadMore } = action.payload;
      state.consultantAvailabilityRows = loadMore
        ? [...state.consultantAvailabilityRows, ...rows]
        : rows;
    },
    setConsultantAvailabilityPagination: (
      state,
      action: PayloadAction<{ page: number; totalPage: number; total: number }>,
    ) => {
      state.consultantAvailabilityPage = action.payload.page;
      state.consultantAvailabilityTotalPage = action.payload.totalPage;
      state.consultantAvailabilityTotal = action.payload.total;
    },
  },
});

export const { actions, reducer: DashboardReducer } = slice;
export const {
  setLoading,
  setLoadingCanceledReservations,
  setLoadingPendingSurveys,
  setLoadingLowReservationRates,
  setLoadingEventProgress,
  setLoadingMoreEventProgress,
  setLoadingInterviewProgress,
  setLoadingMoreInterviewProgress,
  setLoadingConsultantAvailability,
  setLoadingMoreConsultantAvailability,
  setTopCounts,
  setCanceledReservations,
  setPendingSurveys,
  setLowReservationRates,
  setEventProgressSummary,
  setEventProgressRows,
  setEventProgressPagination,
  setInterviewProgressRows,
  setInterviewProgressPagination,
  setConsultantAvailabilityRows,
  setConsultantAvailabilityPagination,
} = actions;
export default DashboardReducer;
